import { FC, useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'antd';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDataSources } from '@/hooks/useDataSources';
import { useMaps } from '@/hooks/useMaps';
import { useConnectedAccountStore } from '@/stores';
import { ConnectedAccountType, SetStateMethod } from '@/types';

import Sider from './layout/01-Sider';
import Content from './layout/02-Content';

interface AddDataSourceModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    activeView?: 'add' | 'manage';
    zIndex?: number;
}

const AddDataSourceModal: FC<AddDataSourceModalProps> = ({ open, setOpen, activeView, zIndex }): JSX.Element => {
    const [view, setView] = useState<'add' | 'manage'>(activeView ?? 'add');
    const [option, setOption] = useState('emailAndCalendar');
    const [input, setInput] = useState('');

    const { token } = useAntDesignTheme();

    const { dataSources } = useDataSources();

    const {
        connectedAccounts,
        deleteConnectedAccount,
        fetchConnectedAccounts,
        loading: connectedAccountsLoading,
    } = useConnectedAccountStore();
    const [hasRequestedConnectedAccounts, setHasRequestedConnectedAccounts] = useState(false);

    const { integrationLabelToIconMap } = useMaps();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const removeAccount = (connectedAccountId: string) => {
        deleteConnectedAccount(connectedAccountId).then(() => {
            toast('Connected Account Removed', {});
        });
    };

     
    const managedSources = useMemo(() => {
        return dataSources
            .map((section) => {
                const accountsInSection = connectedAccounts
                    .filter((acc) => section.options.some((opt) => opt.key === acc.type))
                    .map((acc) => {
                        const matchingOption = section.options.find((opt) => opt.key === acc.type);

                        if (!matchingOption) {return null;}

                        return {
                            ...matchingOption,
                            key: acc.id,
                            type: acc.type,
                            label: acc.nickname ?? matchingOption.label,
                            src: integrationLabelToIconMap[acc.type as ConnectedAccountType],
                            createdAt: acc.createdAt,
                            updatedAt: acc.updatedAt,
                            connectedAccount: acc,
                            handler: () => {},
                            onRemove: () => removeAccount(acc.id),
                        };
                    })
                    .filter(Boolean);

                return { ...section, options: accountsInSection };
            })
            .filter((section) => section.options.length > 0);
    }, [dataSources, connectedAccounts, integrationLabelToIconMap, removeAccount]);

    const selectedData = view === 'add' ? dataSources : managedSources;

    let options = [];

    if (option === 'all') {
        options = selectedData.flatMap((s) => (s.options as any) || []);
    } else {
        const selectedSection = selectedData.find((s) => s.key === option);

        options = selectedSection?.options || [];
    }

    const normalizedInput = input.trim().toLowerCase();

    const filteredOptions =
        normalizedInput === ''
            ? options
            : options.filter((opt: any) => {
                  const labelMatch = String(opt.label ?? '')
                      .toLowerCase()
                      .includes(normalizedInput);

                  const categoryMatch =
                      Array.isArray(opt.categories) &&
                      opt.categories.some((c: any) =>
                          String(c?.label ?? '')
                              .toLowerCase()
                              .includes(normalizedInput),
                      );

                  return labelMatch || categoryMatch;
              });

    useEffect(() => {
        setInput('');
    }, [view, option]);

    useEffect(() => {
        if (open) {
            setView(activeView ?? 'add');
        }
    }, [activeView, open]);

    useEffect(() => {
        if (open && !hasRequestedConnectedAccounts && connectedAccounts.length === 0) {
            setHasRequestedConnectedAccounts(true);
            void fetchConnectedAccounts();
        }
    }, [connectedAccounts.length, fetchConnectedAccounts, hasRequestedConnectedAccounts, open]);

    return (
        <Modal
            destroyOnHidden
            mask={false}
            open={open}
            zIndex={zIndex}
            // title='Add Data Source'
            closable={false}
            closeIcon={null}
            width='min(60rem, calc(100vw - 2rem))'
            style={{
                minHeight: 'min(50rem, calc(100vh - 4rem))',
                minWidth: 'min(60rem, calc(100vw - 2rem))',
            }}
            styles={{
                body: {
                    borderBottom: 'none',
                    height: 'min(50rem, calc(100vh - 4rem))',
                    minHeight: 'min(50rem, calc(100vh - 4rem))',
                    overflow: 'hidden',
                },
                content: {
                    padding: 0,
                    margin: 0,
                    border: `1px solid ${token.colorBorder}`,
                },
            }}
            footer={null}
            onCancel={() => setOpen(false)}
        >
            <Row wrap={false} style={{ height: '100%', minHeight: 0 }}>
                <Col span={8} style={{ height: '100%', minHeight: 0 }}>
                    <div
                        style={{
                            height: '100%',
                        }}
                    >
                        <Sider
                            option={option}
                            setOption={setOption}
                            view={view}
                            setView={setView}
                            connectedAccounts={connectedAccounts}
                        />
                    </div>
                </Col>
                <Col flex='auto' style={{ borderLeft: `1px solid ${token.colorBorder}`, height: '100%', minHeight: 0 }}>
                    <div style={{ boxSizing: 'border-box', padding: '1rem', height: '100%', minHeight: 0 }}>
                        <Content
                            view={view}
                            options={filteredOptions}
                            loading={view === 'manage' && connectedAccountsLoading && connectedAccounts.length === 0}
                            option={option}
                            setOpen={setOpen}
                            input={input}
                            setInput={setInput}
                        />
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default AddDataSourceModal;
